import {
  mapVenueOverviewVersionsResponseType,
  PermissionNameUnionType,
  useFindAllVenueOverviewVersionsSuspense,
} from '@api';
import { VersionSelect } from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Route } from 'src/routes/_rootLayout/venues/$id.overview.$version';
import {
  useAddOverviewVersion,
  useDeleteOverviewVersion,
  useEditOverviewVersion,
} from './hooks';

export const VenueOverviewVersionSelect: React.FC = () => {
  const { id, version: selectedVersion } = Route.useParams();
  const { handleDelete } = useDeleteOverviewVersion({ venueId: id });
  const { editVersion } = useEditOverviewVersion({ venueId: id });
  const { addVersion } = useAddOverviewVersion({ venueId: id });
  const { hasPermission } = usePermissionContext<PermissionNameUnionType>();

  const { data: versions } = useFindAllVenueOverviewVersionsSuspense(id, {
    query: {
      select: (data) => mapVenueOverviewVersionsResponseType(data),
    },
  });

  const previousVersion = versions[versions.indexOf(selectedVersion ?? '') - 1];
  const nextVersion = versions[versions.indexOf(selectedVersion ?? '') + 1];

  const handleEditVersion = async (data: { version: string }) => {
    await editVersion({
      oldVersion: selectedVersion,
      newVersion: data.version,
    });
  };

  const handleAddVersion = async (data: { version: string }) => {
    await addVersion({ newVersion: data.version });
  };

  const userCanEditVenueOverview = hasPermission({
    permission: 'VENUES_OVERVIEW',
    action: 'WRITE',
  });

  return (
    <VersionSelect selectedVersion={selectedVersion}>
      <VersionSelect.PreviousVersion
        to={Route.fullPath}
        params={{ id, version: previousVersion }}
        disabled={!previousVersion}
      />
      <VersionSelect.Combobox
        versions={versions}
        routePath={Route.fullPath}
        urlParams={{ id }}
      />
      <VersionSelect.NextVersion
        to={Route.fullPath}
        params={{ id, version: nextVersion }}
        disabled={!nextVersion}
      />
      {userCanEditVenueOverview && (
        <VersionSelect.ContextMenu
          selectedVersion={selectedVersion}
          handleSaveEdit={handleEditVersion}
          handleSaveNew={handleAddVersion}
          handleDelete={handleDelete}
        >
          <VersionSelect.ContextMenu.Edit />
          <VersionSelect.ContextMenu.New />
          <VersionSelect.ContextMenu.Delete disabled={versions.length === 1} />
        </VersionSelect.ContextMenu>
      )}
    </VersionSelect>
  );
};
